// OBJECT PICKER — pick an existing object by searching for it (2026-08-07).
//
// Extracted from the inline search in ObjectDetail's relation form, which was
// the only copy of a pattern the thread editor needed twice (add a step, splice
// a step in). Two things about it are subtle enough that a second hand-written
// copy would get one of them wrong:
//
//   1. DEBOUNCE, so typing does not issue a request per keystroke.
//   2. A SEQUENCE GUARD, so a slow response for "cli" cannot overwrite a newer
//      list for "climate". Guarded rather than cancelled, deliberately —
//      cancelled work is silently dropped work, and an aborted request that
//      turns out to have been the newest leaves the field empty for no visible
//      reason.
//
// The alternative this replaces in the thread editor was `window.prompt` asking
// for a pasted object id, which is not a affordance so much as an admission
// that one was missing.
import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from './config';

// MACHINERY is not content, and offering it as a choice is how a nonsense edge
// gets drawn (caught on the first drive of the thread editor: the picker listed
// the `then` and `starts_at` relation HANDLES, so the top offer was to draw an
// edge at the plumbing).
//
// Filtered here rather than by widening `UNLISTED_OBJECT_TYPES` on the search
// route, because that set governs BROWSE, where a relation handle is a
// legitimate thing to look at — it is how the convergence layer is inspected.
// What may be BROWSED and what may be PICKED as content are different questions
// and this is the one asking the second.
//
// `thread` LEFT this list 2026-08-08, and the distinction is worth stating
// because putting it back would look like a fix. It was added for one real
// defect — the picker offered the thread being edited, so the top suggestion was
// to make a thread a step in itself — but that is a SELF-reference problem, and
// `excludeIds` is what solves it. Excluding the whole TYPE also refused every
// OTHER thread, which is the thing that makes a long review readable: a spine of
// chapter threads, each of which opens as its own page with its own share link.
// One narrow exclusion by id; not a broad one by type.
const MACHINERY_TYPES = ['relation', 'edge', 'snapshot', 'group'];

export default function ObjectPicker({
  onPick,
  onCancel,
  placeholder = 'search objects…',
  limit = 20,
  autoFocus = true,
  excludeTypes = MACHINERY_TYPES,
  excludeIds = [],
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const mySeq = ++seq.current;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        // Over-fetch, because the filter below runs client-side and a page of
        // results that is mostly machinery would otherwise come back empty.
        const params = new URLSearchParams({ limit: String(Math.min(100, limit * 3)) });
        if (q.trim()) params.set('q', q.trim());
        const res = await apiFetch(`/api/objects?${params.toString()}`);
        if (!res.ok || mySeq !== seq.current) return;
        const rows = await res.json();
        if (mySeq !== seq.current) return;
        setResults(
          rows
            .filter((o) => !excludeTypes.includes(o.object_type) && !excludeIds.includes(o.id))
            .slice(0, limit)
        );
      } catch (err) {
        console.error('ObjectPicker search failed:', err);
      } finally {
        if (mySeq === seq.current) setBusy(false);
      }
    }, q.trim() ? 200 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, limit, excludeTypes.join(','), excludeIds.join(',')]);

  return (
    <div className="wv-panel" style={{ padding: 'var(--wv-sp-2)', marginTop: 'var(--wv-sp-2)' }}>
      <div style={{ display: 'flex', gap: 'var(--wv-sp-2)' }}>
        <input
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          style={{ flex: 1, fontSize: 'var(--wv-fs-sm)' }}
        />
        {onCancel && (
          <button className="wv-chip" onClick={onCancel}>
            cancel
          </button>
        )}
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 'var(--wv-sp-2)' }}>
        {busy && !results.length && (
          <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>searching…</div>
        )}
        {!busy && !results.length && (
          <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>nothing matches</div>
        )}
        {results.map((o) => (
          <button
            key={o.id}
            onClick={() => onPick(o)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: 'var(--wv-sp-1) var(--wv-sp-2)',
              fontSize: 'var(--wv-fs-sm)',
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginRight: 6 }}>
              {o.object_type}
            </span>
            {String(o.content || '').slice(0, 110)}
          </button>
        ))}
      </div>
    </div>
  );
}
